import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import useMediaQuery from "../hooks/useMediaQuery.js";
import logoImage from "./assets/Logo/0.02.png";

const NavLink = ({ page, selectedPage, setSelectedPage, onNavigate }) => {
  const location = useLocation();
  const lowerCasePage = page.toLowerCase().replace(/\s+/g, '-');
  const isActive = location.pathname === `/${lowerCasePage}` || 
    (location.pathname === '/' && lowerCasePage === 'home');
  
  return (
    <Link
      to={lowerCasePage === 'home' ? '/' : `/${lowerCasePage}`}
      className={`${
        isActive ? "text-gold font-bold" : "text-dark-grey"
      } hover:text-gold transition-all duration-300 hover:scale-105 relative group`}
      onClick={() => {
        setSelectedPage(lowerCasePage);
        onNavigate?.();
      }}
      aria-current={isActive ? 'page' : undefined}
    >
      {page}
      {/* Active state underline */}
      {isActive && (
        <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gold rounded-full"></div>
      )}
      {/* Hover underline */}
      <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gold rounded-full group-hover:w-full transition-all duration-300"></div>
    </Link>
  );
};

const TherapyDropdown = ({ isOpen, onClose }) => {
  const [bioresonanceSubMenuOpen, setBioresonanceSubMenuOpen] = useState(false);
  const subMenuTimeoutRef = useRef(null);
  const treatments = [
    { name: 'All Services', path: '/therapy' },
    { name: 'NLS Hunter', path: '/therapy/nls-hunter' },
    { name: 'Red Light Sessions with Helios', path: '/therapy/red-light-sessions' },
    { name: 'BEMER Sessions', path: '/therapy/bemer-sessions' },
    { name: 'Targeted Radio Frequency', path: '/therapy/tr-therapy' },
    { name: 'HydroVitality', path: '/therapy/hydro-vitality' },
    { name: 'Swedish Massage', path: '/therapy/swedish-massage' },
    { name: 'Cupping Therapy', path: '/therapy/cupping-therapy' },
    { name: 'Bioresonance Therapy', path: '/therapy/bioresonance-therapy', hasSubMenu: true }
  ];

  const openSubMenu = () => {
    if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
    setBioresonanceSubMenuOpen(true);
  };
  const closeSubMenu = () => {
    subMenuTimeoutRef.current = setTimeout(() => setBioresonanceSubMenuOpen(false), 150);
  };

  return (
    <div
      id="therapy-dropdown"
      role="menu"
      className={`absolute top-full left-0 w-48 bg-white rounded-lg shadow-lg border border-grey/20 transition-all duration-300 ${
        isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
      }`}
      style={{ marginTop: '4px' }}
    >
      {treatments.map((treatment, index) => (
        <div key={index} className="relative" role="none">
          {treatment.hasSubMenu ? (
            <div
              className="flex items-center justify-between px-4 py-3 text-dark-grey hover:bg-gold/10 hover:text-gold transition-colors duration-200"
              onMouseEnter={openSubMenu}
              onMouseLeave={closeSubMenu}
              onFocus={openSubMenu}
              onBlur={closeSubMenu}
            >
              <Link
                to={treatment.path}
                className="flex-1"
                onClick={onClose}
                role="menuitem"
              >
                {treatment.name}
              </Link>
              <span className="ml-2 p-1 text-gold" aria-hidden="true">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          ) : (
            <Link
              to={treatment.path}
              className="block px-4 py-3 text-dark-grey hover:bg-gold/10 hover:text-gold transition-colors duration-200"
              onClick={onClose}
              role="menuitem"
            >
              {treatment.name}
            </Link>
          )}
          
          {/* Submenu - positioned below */}
          {treatment.hasSubMenu && bioresonanceSubMenuOpen && (
            <div
              className="absolute left-0 top-full px-4 py-3 bg-white border border-gold/30 rounded-lg shadow-lg min-w-56 z-50 mt-1"
              role="menu"
              onMouseEnter={openSubMenu}
              onMouseLeave={closeSubMenu}
              onFocus={openSubMenu}
              onBlur={closeSubMenu}
            >
              <div className="text-sm font-semibold text-gold mb-3 border-b border-gold/20 pb-2">Sub-treatments:</div>
              <div className="space-y-2" role="group" aria-label="Bioresonance sub-treatments">
                <Link to="/therapy/bioresonance-therapy/allergies" className="block text-sm text-dark-grey hover:text-gold hover:bg-gold/5 px-2 py-1 rounded transition-colors duration-200" onClick={onClose} role="menuitem">• Allergies</Link>
                <Link to="/therapy/bioresonance-therapy/addictions" className="block text-sm text-dark-grey hover:text-gold hover:bg-gold/5 px-2 py-1 rounded transition-colors duration-200" onClick={onClose} role="menuitem">• Addictions</Link>
                <Link to="/therapy/bioresonance-therapy/depression" className="block text-sm text-dark-grey hover:text-gold hover:bg-gold/5 px-2 py-1 rounded transition-colors duration-200" onClick={onClose} role="menuitem">• Depression</Link>
                <Link to="/therapy/bioresonance-therapy/skin-problems" className="block text-sm text-dark-grey hover:text-gold hover:bg-gold/5 px-2 py-1 rounded transition-colors duration-200" onClick={onClose} role="menuitem">• Skin Problems</Link>
                <Link to="/therapy/bioresonance-therapy/lyme-disease" className="block text-sm text-dark-grey hover:text-gold hover:bg-gold/5 px-2 py-1 rounded transition-colors duration-200" onClick={onClose} role="menuitem">• Lyme Disease</Link>
                <Link to="/therapy/bioresonance-therapy/arthritis" className="block text-sm text-dark-grey hover:text-gold hover:bg-gold/5 px-2 py-1 rounded transition-colors duration-200" onClick={onClose} role="menuitem">• Arthritis</Link>
                <Link to="/therapy/bioresonance-therapy/muscle-joint-spine" className="block text-sm text-dark-grey hover:text-gold hover:bg-gold/5 px-2 py-1 rounded transition-colors duration-200" onClick={onClose} role="menuitem">• Muscle, Joint and Spine Pain</Link>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Navbar = ({ selectedPage, setSelectedPage }) => {
  const [isMenuToggled, setIsMenuToggled] = useState(false);
  const [isTherapyDropdownOpen, setIsTherapyDropdownOpen] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState(null);
  const [activeLanguage, setActiveLanguage] = useState('gb'); // Default to GB (English)
  const isAboveSmallScreens = useMediaQuery("(min-width: 768px)");
  const location = useLocation();
  const therapyButtonRef = useRef(null);
  const therapyDropdownWrapperRef = useRef(null);

  const handleMouseEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setIsTherapyDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsTherapyDropdownOpen(false);
    }, 150);
    setDropdownTimeout(timeout);
  };

  const handleTherapyKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsTherapyDropdownOpen(false);
      therapyButtonRef.current?.focus();
      return;
    }
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setIsTherapyDropdownOpen(true);
    }
  };

  useEffect(() => {
    if (!isTherapyDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        therapyDropdownWrapperRef.current &&
        !therapyDropdownWrapperRef.current.contains(e.target)
      ) {
        setIsTherapyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTherapyDropdownOpen]);

  return (
    <nav 
      className="z-40 w-full fixed top-0 shadow-lg bg-white"
      style={{
        backdropFilter: 'blur(10px)',
        borderBottom: '2px solid rgba(184,139,68,0.3)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}
    >
      {/* set a fixed navbar height */}
      <div className="h-16 md:h-20">
        <div className="flex items-center justify-between h-full mx-auto w-11/12 max-w-9xl px-4 md:px-8 lg:px-16">
        <Link 
          to="/" 
          className="cursor-pointer hover:opacity-80 transition-opacity duration-300"
          onClick={() => {
            setSelectedPage('home');
            if (!isAboveSmallScreens) setIsMenuToggled(false);
          }}
        >
          <img 
            src={logoImage} 
            alt="Bioresonance Logo" 
            className="block h-36 md:h-42 lg:h-48 max-h-full w-auto object-contain"
          />
        </Link>

        {/* DESKTOP NAV */}
        {isAboveSmallScreens ? (
          <div className="flex items-center gap-6 lg:gap-10 font-opensans text-base font-medium">
            <NavLink
              page="Home"
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
            />
            
            {/* Services Dropdown */}
            <div
              ref={therapyDropdownWrapperRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                ref={therapyButtonRef}
                type="button"
                aria-expanded={isTherapyDropdownOpen}
                aria-haspopup="true"
                aria-controls="therapy-dropdown"
                className={`${
                  location.pathname.startsWith('/therapy') ? "text-gold font-bold" : "text-dark-grey"
                } hover:text-gold transition-all duration-300 hover:scale-105 relative group flex items-center`}
                onClick={() => setIsTherapyDropdownOpen((prev) => !prev)}
                onKeyDown={handleTherapyKeyDown}
                aria-current={location.pathname.startsWith('/therapy') ? 'true' : undefined}
              >
                Services
                <svg className="ml-1 w-4 h-4 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                {/* Active state underline */}
                {location.pathname.startsWith('/therapy') && (
                  <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gold rounded-full"></div>
                )}
                {/* Hover underline */}
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gold rounded-full group-hover:w-full transition-all duration-300"></div>
              </button>
              <TherapyDropdown 
                isOpen={isTherapyDropdownOpen} 
                onClose={() => setIsTherapyDropdownOpen(false)}
              />
            </div>
            
            <NavLink
              page="Prices"
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
            />
            <NavLink
              page="Gallery"
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
            />
            <NavLink
              page="About Us"
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
            />
            <NavLink
              page="Contact"
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
            />
            <NavLink
              page="FAQ"
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
            />
            
            {/* Language Flags */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-grey/30">
              <div role="group" aria-label="Language">
                <button 
                  type="button"
                  className={`text-2xl hover:scale-110 transition-all duration-200 cursor-pointer p-2 rounded-lg ${
                    activeLanguage === 'gb' 
                      ? 'bg-gold/10 shadow-md shadow-gold/15 border border-gold/20' 
                      : 'hover:bg-grey/10 hover:shadow-sm'
                  }`}
                  title="English"
                  aria-pressed={activeLanguage === 'gb'}
                  onClick={() => setActiveLanguage('gb')}
                >
                  🇬🇧
                </button>
                <button 
                  type="button"
                  className={`text-2xl hover:scale-110 transition-all duration-200 cursor-pointer p-2 rounded-lg ${
                    activeLanguage === 'pl' 
                      ? 'bg-gold/10 shadow-md shadow-gold/15 border border-gold/20' 
                      : 'hover:bg-grey/10 hover:shadow-sm'
                  }`}
                  title="Polski"
                  aria-pressed={activeLanguage === 'pl'}
                  onClick={() => setActiveLanguage('pl')}
                >
                  🇵🇱
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-dark-grey">
            <button
              type="button"
              className="text-2xl hover:text-gold transition-colors duration-300"
              onClick={() => setIsMenuToggled(!isMenuToggled)}
              aria-expanded={isMenuToggled}
              aria-label={isMenuToggled ? 'Close menu' : 'Open menu'}
            >
              ☰
            </button>
          </div>
        )}

        {/* MOBILE MENU */}
        {!isAboveSmallScreens && isMenuToggled && (
          <div 
            className="absolute top-full left-0 right-0 mx-0 w-full bg-white border-t border-gold/30 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.2)] rounded-b-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
            style={{ animation: 'slideDown 0.3s ease-out' }}
          >
            <div className="flex flex-col items-stretch w-full py-5 px-6">
              {/* Main nav links - left-aligned, larger tap targets */}
              <div className="flex flex-col gap-0 text-left border-b border-grey/15 pb-4 mb-2">
                <div className="py-3.5 text-[17px] font-medium">
                  <NavLink
                    page="Home"
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                    onNavigate={() => setIsMenuToggled(false)}
                  />
                </div>
                
                {/* Mobile Services Section */}
                <div className="w-full text-left pt-1">
                  <Link
                    to="/therapy"
                    className="block py-3.5 text-[17px] font-medium text-dark-grey hover:text-gold transition-colors duration-200"
                    onClick={() => setIsMenuToggled(false)}
                  >
                    Services
                  </Link>
                  <div className="pl-3 border-l-2 border-gold/20 space-y-0.5">
                    <Link
                      to="/therapy/nls-hunter"
                      className="block py-2.5 text-[15px] text-dark-grey hover:text-gold transition-colors duration-200"
                      onClick={() => setIsMenuToggled(false)}
                    >
                      NLS Hunter
                    </Link>
                    <Link
                      to="/therapy/red-light-sessions"
                      className="block py-2.5 text-[15px] text-dark-grey hover:text-gold transition-colors duration-200"
                      onClick={() => setIsMenuToggled(false)}
                    >
                      Red Light Sessions with Helios
                    </Link>
                    <Link
                      to="/therapy/bemer-sessions"
                      className="block py-2.5 text-[15px] text-dark-grey hover:text-gold transition-colors duration-200"
                      onClick={() => setIsMenuToggled(false)}
                    >
                      BEMER Sessions
                    </Link>
                    <Link
                      to="/therapy/tr-therapy"
                      className="block py-2.5 text-[15px] text-dark-grey hover:text-gold transition-colors duration-200"
                      onClick={() => setIsMenuToggled(false)}
                    >
                      Targeted Radio Frequency
                    </Link>
                    <Link
                      to="/therapy/hydro-vitality"
                      className="block py-2.5 text-[15px] text-dark-grey hover:text-gold transition-colors duration-200"
                      onClick={() => setIsMenuToggled(false)}
                    >
                      HydroVitality
                    </Link>
                    <Link
                      to="/therapy/swedish-massage"
                      className="block py-2.5 text-[15px] text-dark-grey hover:text-gold transition-colors duration-200"
                      onClick={() => setIsMenuToggled(false)}
                    >
                      Swedish Massage
                    </Link>
                    <Link
                      to="/therapy/cupping-therapy"
                      className="block py-2.5 text-[15px] text-dark-grey hover:text-gold transition-colors duration-200"
                      onClick={() => setIsMenuToggled(false)}
                    >
                      Cupping Therapy
                    </Link>
                    <div className="pt-2">
                      <Link
                        to="/therapy/bioresonance-therapy"
                        className="block py-2.5 text-[15px] font-medium text-dark-grey hover:text-gold transition-colors duration-200"
                        onClick={() => setIsMenuToggled(false)}
                      >
                        Bioresonance Therapy
                      </Link>
                      <div className="pl-3 mt-1 space-y-0.5 border-l-2 border-gold/10">
                        <Link
                          to="/therapy/bioresonance-therapy/allergies"
                          className="block py-2 text-[14px] text-dark-grey hover:text-gold transition-colors duration-200"
                          onClick={() => setIsMenuToggled(false)}
                        >
                          Allergies
                        </Link>
                        <Link
                          to="/therapy/bioresonance-therapy/addictions"
                          className="block py-2 text-[14px] text-dark-grey hover:text-gold transition-colors duration-200"
                          onClick={() => setIsMenuToggled(false)}
                        >
                          Addictions
                        </Link>
                        <Link
                          to="/therapy/bioresonance-therapy/depression"
                          className="block py-2 text-[14px] text-dark-grey hover:text-gold transition-colors duration-200"
                          onClick={() => setIsMenuToggled(false)}
                        >
                          Depression
                        </Link>
                        <Link
                          to="/therapy/bioresonance-therapy/skin-problems"
                          className="block py-2 text-[14px] text-dark-grey hover:text-gold transition-colors duration-200"
                          onClick={() => setIsMenuToggled(false)}
                        >
                          Skin Problems
                        </Link>
                        <Link
                          to="/therapy/bioresonance-therapy/lyme-disease"
                          className="block py-2 text-[14px] text-dark-grey hover:text-gold transition-colors duration-200"
                          onClick={() => setIsMenuToggled(false)}
                        >
                          Lyme Disease
                        </Link>
                        <Link
                          to="/therapy/bioresonance-therapy/arthritis"
                          className="block py-2 text-[14px] text-dark-grey hover:text-gold transition-colors duration-200"
                          onClick={() => setIsMenuToggled(false)}
                        >
                          Arthritis
                        </Link>
                        <Link
                          to="/therapy/bioresonance-therapy/muscle-joint-spine"
                          className="block py-2 text-[14px] text-dark-grey hover:text-gold transition-colors duration-200"
                          onClick={() => setIsMenuToggled(false)}
                        >
                          Muscle, Joint and Spine Pain
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col gap-0 text-left border-b border-grey/15 pb-4">
                <div className="py-3.5 text-[17px] font-medium">
                  <NavLink
                    page="Prices"
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                    onNavigate={() => setIsMenuToggled(false)}
                  />
                </div>
                <div className="py-3.5 text-[17px] font-medium">
                  <NavLink
                    page="Gallery"
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                    onNavigate={() => setIsMenuToggled(false)}
                  />
                </div>
                <div className="py-3.5 text-[17px] font-medium">
                  <NavLink
                    page="About Us"
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                    onNavigate={() => setIsMenuToggled(false)}
                  />
                </div>
                <div className="py-3.5 text-[17px] font-medium">
                  <NavLink
                    page="Contact"
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                    onNavigate={() => setIsMenuToggled(false)}
                  />
                </div>
                <div className="py-3.5 text-[17px] font-medium">
                  <NavLink
                    page="FAQ"
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                    onNavigate={() => setIsMenuToggled(false)}
                  />
                </div>
              </div>
              
              {/* Mobile Language Flags */}
              <div className="flex items-center justify-center gap-5 pt-5" role="group" aria-label="Language">
                <button 
                  type="button"
                  className={`text-2xl hover:scale-110 transition-all duration-200 cursor-pointer p-3 rounded-xl ${
                    activeLanguage === 'gb' 
                      ? 'bg-gold/10 shadow-md shadow-gold/15 border border-gold/20' 
                      : 'hover:bg-grey/10 hover:shadow-sm'
                  }`}
                  title="English"
                  aria-pressed={activeLanguage === 'gb'}
                  onClick={() => setActiveLanguage('gb')}
                >
                  🇬🇧
                </button>
                <button 
                  type="button"
                  className={`text-2xl hover:scale-110 transition-all duration-200 cursor-pointer p-3 rounded-xl ${
                    activeLanguage === 'pl' 
                      ? 'bg-gold/10 shadow-md shadow-gold/15 border border-gold/20' 
                      : 'hover:bg-grey/10 hover:shadow-sm'
                  }`}
                  title="Polski"
                  aria-pressed={activeLanguage === 'pl'}
                  onClick={() => setActiveLanguage('pl')}
                >
                  🇵🇱
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 